import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/utils/api';

export interface User {
    uuid: string;
    userName: string;
    userEmail: string;
    user_active: boolean;
    user_pause: boolean;
    daily_lead_cap?: number;
    daily_order_processing_cap?: number;
    daily_reorder_cap?: number;
    daily_rto_cap?: number;
    role?: {
        role_name: string;
    };
}

interface UsersContextType {
    users: User[];
    activeUsers: User[];
    reorderTeamUsers: User[];
    rtoTeamUsers: User[];
    loading: boolean;
    error: Error | null;
    refreshUsers: () => Promise<void>;
}

const UsersContext = createContext<UsersContextType | undefined>(undefined);

export const UsersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const location = useLocation();

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.req({
                url: '/users?limit=100',
                method: api.Method.GET,
            });

            if (response.status === 'success' && response.data) {
                const data = response.data as any;
                const usersArray = (data && typeof data === 'object' && 'users' in data) ? data.users : [];
                setUsers(usersArray);
                setHasLoaded(true);
            }
        } catch (err) {
            console.error('❌ UsersContext: Failed to fetch users:', err);
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    };

    // Load users when needed, skip on settings/users page (it has its own fetch with pagination)
    useEffect(() => {
        // Skip if already loaded
        if (hasLoaded) {
            return;
        }

        // Skip initial load on settings/users page - it fetches its own data
        if (location.pathname === '/settings/users') {
            setLoading(false);
            return;
        }

        // Load users for other pages that need the dropdown data
        loadUsers();
    }, [location.pathname]); // Re-check when route changes

    // Computed property: active users
    const activeUsers = users.filter(u => u.user_active === true && u.user_pause === false);

    // Computed property: reorder team users (active users with daily_reorder_cap > 0)
    const reorderTeamUsers = activeUsers.filter(u => u.daily_reorder_cap && u.daily_reorder_cap > 0);

    // Computed property: RTO team users (active users with daily_rto_cap > 0)
    const rtoTeamUsers = activeUsers.filter(u => u.daily_rto_cap && u.daily_rto_cap > 0);

    const refreshUsers = async () => {
        await loadUsers();
    };

    return (
        <UsersContext.Provider value={{ users, activeUsers, reorderTeamUsers, rtoTeamUsers, loading, error, refreshUsers }}>
            {children}
        </UsersContext.Provider>
    );
};

// Custom hook to use the users context
export const useUsers = () => {
    const context = useContext(UsersContext);
    if (context === undefined) {
        throw new Error('useUsers must be used within a UsersProvider');
    }
    return context;
};
